import React, { useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Card, Badge, Button, Nav } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import web5 from "../img/jbl.jpg";

const JewSof = () => {
  const navigate = useNavigate();

  // ===================== THEME =====================
  const theme = useMemo(
    () => ({
      bg: "#f6f8fb",
      surface: "#ffffff",
      ink: "#0f172a",
      slate: "#334155",
      muted: "#64748b",
      border: "#e5e7eb",
      navy: "#0b1220",
      accent: "#1d4ed8",
      accent2: "#0ea5e9",
      success: "#16a34a",
      shadow: "0 24px 48px rgba(2, 6, 23, 0.08)",
      shadowSoft: "0 12px 24px rgba(2, 6, 23, 0.06)",
    }),
    []
  );

  // ===================== CONFIG =====================
  const brand = useMemo(
    () => ({
      name: "EBS Software Solution",
      email: "info@ebssoftware.co.in",
      phoneTop: "+91 99320 13501",
      salesPhone: "+91 97337 2 1111",
      gst: "19AJAPM8399K1ZE",
      websiteUrl: "https://example.com", // change to your real site
      pageUrl: "https://example.com/jewsof", // change to your live URL
    }),
    []
  );

  const product = useMemo(
    () => ({
      name: "JewSof",
      tagline: "GST Enabled Jewellery Billing, Inventory & Accounting",
      description:
        "JewSof is a proven Jewellery Management System for billing, inventory and accounting—designed for high accuracy, barcode/QR support, order handling, karigar workflows and strong reporting.",
      price: "Rs. 45,000/- (1 licence) Multi-User + GST",
      os: ["Windows"],
    }),
    []
  );

  // ===================== CONTENT =====================
  const heroTags = useMemo(
    () => ["GST Billing", "Inventory", "Accounting", "Karigar", "Orders", "Barcode/QR"],
    []
  );

  const trustStats = useMemo(
    () => [
      { label: "Best For", value: "Jewellery Outlets & Showrooms" },
      { label: "Core Suite", value: "Billing + Inventory + Accounts" },
      { label: "Workflows", value: "Orders + Karigar + Stock" },
      { label: "Support", value: "Barcode / QR + Reports" },
    ],
    []
  );

  const highlights = useMemo(
    () => [
      {
        title: "High-Accuracy Jewellery Billing",
        desc: "GST-ready billing, quotations/estimates, sales return and payment-mode reporting.",
      },
      {
        title: "Order & Customer Advance Tracking",
        desc: "Customer orders, advances (gold/amount), cancellations and pending order registers.",
      },
      {
        title: "Karigar (Worker) Workflow",
        desc: "Issue/receipt for unfinished & finished ornaments, charges tracking and job registers.",
      },
      {
        title: "Stock Visibility",
        desc: "Raw, finished, old gold, soldering and unfinished product stock with closing registers.",
      },
    ],
    []
  );

  const featureGroups = useMemo(
    () => [
      {
        title: "Contractual Worker (Karigar)",
        bullets: [
          "Gold Issue to Karigar",
          "Unfinished Ornaments Receipt from Karigar",
          "Unfinished Ornaments Issue to Goldsmith (Karigar)",
          "Finished Ornaments Receipt from Goldsmith (Karigar)",
          "Soldering Making",
          "Old Gold Converts to Pure Gold (Tezabi)",
          "Worker (Karigar) Details/Summary Transaction",
          "Pending Jobs (Order / Self) Register",
          "Worker Charges Details (Against Jobs)",
        ],
      },
      {
        title: "Sales",
        bullets: [
          "Customer Order",
          "Advance Against Order (Gold/Amount)",
          "Order Cancellation",
          "Order Sends to Contractual Workers",
          "Order Delivery (Gold/Silver/Diamond/Platinum etc.)",
          "Counter Sales (Gold/Silver/Diamond/Platinum etc.)",
          "Sales Return",
          "Estimate / Quotation",
          "Sales Register (Payment Mode Wise)",
          "Customer Order Register",
          "Pending Customer Order Register",
          "Register of Customer's Gold and Advance",
          "Order Cancellation Register",
          "GST Collection Register",
          "Barcode / QR Code Generated",
        ],
      },
      {
        title: "Purchase",
        bullets: [
          "Raw Gold Purchase",
          "Finished Product Purchase",
          "Finished Product Received from Contractual Workers / Own",
          "Purchase Return",
          "Old Gold Purchase",
          "Old Gold Transfer to Finished Product",
          "Pure Gold Purchase Register",
          "Old Gold Purchase Register",
          "GST ITC Reports",
        ],
      },
      {
        title: "Stock Reports",
        bullets: [
          "Raw Material Stock",
          "Finished Product Stock Summary (Gold/Diamond/Silver/Platinum)",
          "Finished Product Closing Stock (Gold/Diamond/Silver/Platinum)",
          "Old Gold Stock Register",
          "Soldering Stock Register",
          "Unfinished Product Stock Register",
        ],
      },
      {
        title: "Outstanding Reports",
        bullets: [
          "Debtor's Balance Summary",
          "Aging Analysis (Debtors/Creditors)",
          "Creditor's Balance Summary",
        ],
      },
      {
        title: "Accounting Reports",
        bullets: [
          "Cash Bank Book",
          "Journal Book",
          "Purchase Register",
          "Purchase Return Register",
          "Sales Register",
          "Sales Return Register",
          "Ledger",
          "Trial Balance",
          "Trading and Profit & Loss Statement",
          "Bank Reconciliation",
          "Interest Calculation",
          "Confirmation of Accounts",
          "Cost Centre Reports",
          "GST Type Wise Sales Register",
          "GSTR (GSTR-1, GSTR-2, GSTR-3B) Reports",
        ],
      },
    ],
    []
  );

  // ===================== SEO (Meta + JSON-LD, NO Helmet) =====================
  const schemaMarkup = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: brand.name,
          url: brand.websiteUrl,
          email: brand.email,
          telephone: brand.phoneTop,
        },
        {
          "@type": "WebPage",
          "@id": brand.pageUrl,
          url: brand.pageUrl,
          name: `${product.name} | Jewellery Billing & Inventory Software | ${brand.name}`,
          description: product.description,
        },
        {
          "@type": "SoftwareApplication",
          name: product.name,
          applicationCategory: "BusinessApplication",
          operatingSystem: product.os,
          description: product.description,
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
          },
          featureList: [
            "GST Billing & Invoicing",
            "Order & Advance Tracking",
            "Karigar Workflow",
            "Barcode/QR Generation",
            "Stock Registers & Closing Stock",
            "Accounting Reports",
            "GSTR Reports",
          ],
          publisher: { "@type": "Organization", name: brand.name },
        },
      ],
    }),
    [brand, product]
  );

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${product.name} | Jewellery Billing & Inventory Software | ${brand.name}`;

    const head = document.head;
    const cleanup = () => {
      head.querySelectorAll('[data-jewsof="1"]').forEach((n) => n.remove());
    };
    cleanup();

    const addMeta = (attrs) => {
      const el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute("data-jewsof", "1");
      head.appendChild(el);
    };

    const addLink = (attrs) => {
      const el = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute("data-jewsof", "1");
      head.appendChild(el);
    };

    addLink({ rel: "canonical", href: brand.pageUrl });
    addMeta({ name: "description", content: product.description });

    addMeta({ property: "og:type", content: "website" });
    addMeta({ property: "og:title", content: `${product.name} | ${brand.name}` });
    addMeta({ property: "og:description", content: product.description });
    addMeta({ property: "og:url", content: brand.pageUrl });

    addMeta({ name: "twitter:card", content: "summary_large_image" });
    addMeta({ name: "twitter:title", content: `${product.name} | ${brand.name}` });
    addMeta({ name: "twitter:description", content: product.description });

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schemaMarkup);
    script.setAttribute("data-jewsof", "1");
    head.appendChild(script);

    return () => {
      cleanup();
      document.title = prevTitle;
    };
  }, [brand, product, schemaMarkup]);

  // ===================== HELPERS =====================
  const scrollToId = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // ===================== STYLES =====================
  const styles = {
    page: {
      background: theme.bg,
      minHeight: "100vh",
      color: theme.slate,
      fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    },
    topBar: {
      background: theme.navy,
      color: "#e2e8f0",
      fontSize: 13,
      borderBottom: "1px solid rgba(255,255,255,0.08)",
    },
    topBarInner: {
      padding: "8px 0",
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      alignItems: "center",
    },
    topLink: { color: "#e2e8f0", textDecoration: "none", fontWeight: 800 },

    navWrap: {
      position: "sticky",
      top: 0,
      zIndex: 40,
      background: "rgba(246,248,251,0.9)",
      backdropFilter: "blur(12px)",
      borderBottom: `1px solid ${theme.border}`,
    },
    navInner: {
      padding: "14px 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 14,
      flexWrap: "wrap",
    },
    brandRow: { display: "flex", alignItems: "center", gap: 12 },
    logo: {
      width: 44,
      height: 44,
      borderRadius: 14,
      background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})`,
      boxShadow: theme.shadowSoft,
      flexShrink: 0,
    },
    brandName: { margin: 0, fontWeight: 950, color: theme.ink, letterSpacing: -0.2, fontSize: 16 },
    brandSub: { margin: 0, color: theme.muted, fontWeight: 750, fontSize: 12 },

    hero: {
      marginTop: 18,
      borderRadius: 24,
      overflow: "hidden",
      position: "relative",
      background: theme.navy,
      boxShadow: theme.shadow,
      border: `1px solid ${theme.border}`,
    },
    heroImg: {
      width: "100%",
      height: 540,
      objectFit: "cover",
      opacity: 0.24,
      filter: "grayscale(100%) contrast(120%)",
      transform: "scale(1.02)",
    },
    heroOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(900px 600px at 18% 25%, rgba(29,78,216,0.35), transparent 60%), radial-gradient(900px 500px at 85% 15%, rgba(14,165,233,0.25), transparent 55%), linear-gradient(180deg, rgba(11,18,32,0.50), rgba(11,18,32,0.92))",
    },
    heroContent: {
      position: "absolute",
      inset: 0,
      padding: "clamp(18px, 4vw, 56px)",
      display: "flex",
      alignItems: "center",
    },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.18)",
      color: "#dbeafe",
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: 1,
      fontSize: 12,
      width: "fit-content",
    },
    h1: {
      marginTop: 14,
      marginBottom: 10,
      fontSize: "clamp(2.2rem, 4.2vw, 3.6rem)",
      fontWeight: 950,
      color: "#fff",
      letterSpacing: -0.8,
      lineHeight: 1.05,
      textShadow: "0 10px 30px rgba(0,0,0,0.35)",
    },
    heroP: {
      color: "#e2e8f0",
      lineHeight: 1.9,
      fontSize: 16,
      maxWidth: 700,
      marginBottom: 18,
    },
    tagRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 },
    tag: {
      padding: "8px 12px",
      borderRadius: 999,
      color: "#e2e8f0",
      background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.18)",
      fontWeight: 850,
      fontSize: 13,
    },
    heroCard: {
      background: "rgba(255,255,255,0.97)",
      borderRadius: 18,
      border: "1px solid rgba(255,255,255,0.60)",
      boxShadow: "0 30px 70px rgba(2,6,23,0.25)",
    },
    statGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 },
    stat: { border: `1px solid ${theme.border}`, borderRadius: 14, padding: 12, background: theme.surface },
    statLabel: { margin: 0, fontSize: 12, color: theme.muted, fontWeight: 850 },
    statValue: { margin: "6px 0 0 0", fontSize: 14, color: theme.ink, fontWeight: 950, lineHeight: 1.35 },

    section: { padding: "56px 0" },
    sectionTitle: { margin: 0, fontSize: 28, fontWeight: 950, color: theme.ink, letterSpacing: -0.4 },
    sectionSub: { marginTop: 10, marginBottom: 0, color: theme.muted, lineHeight: 1.85, maxWidth: 900 },

    featureCard: {
      height: "100%",
      borderRadius: 18,
      border: `1px solid ${theme.border}`,
      background: theme.surface,
      boxShadow: theme.shadowSoft,
      padding: 18,
    },
    featureTitle: { margin: 0, fontSize: 16, fontWeight: 950, color: theme.ink, letterSpacing: -0.2 },
    featureDesc: { margin: "8px 0 0 0", color: theme.muted, lineHeight: 1.8, fontSize: 14 },

    moduleCard: {
      height: "100%",
      borderRadius: 18,
      border: `1px solid ${theme.border}`,
      background: theme.surface,
      boxShadow: theme.shadowSoft,
      overflow: "hidden",
    },
    moduleHead: {
      padding: 18,
      borderBottom: `1px solid ${theme.border}`,
      background: "linear-gradient(180deg, rgba(29,78,216,0.08), rgba(255,255,255,0))",
    },
    moduleTitle: { margin: 0, fontWeight: 950, color: theme.ink, fontSize: 16 },
    moduleList: { listStyle: "none", margin: 0, padding: 18, display: "grid", gap: 10 },
    moduleItem: { display: "flex", gap: 10, alignItems: "flex-start", color: theme.slate, lineHeight: 1.75, fontSize: 14 },

    cta: {
      borderRadius: 26,
      background: theme.navy,
      color: "#fff",
      position: "relative",
      overflow: "hidden",
      boxShadow: theme.shadow,
      border: "1px solid rgba(255,255,255,0.12)",
      marginTop: 8,
    },
    ctaOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(900px 500px at 20% 30%, rgba(29,78,216,0.45), transparent 60%), radial-gradient(800px 450px at 80% 20%, rgba(14,165,233,0.30), transparent 55%), linear-gradient(180deg, rgba(11,18,32,0.35), rgba(11,18,32,0.92))",
    },
    ctaBody: {
      position: "relative",
      padding: "30px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
    },
    ctaTitle: {
      margin: 0,
      fontWeight: 950,
      letterSpacing: -0.4,
      color: "#ffffff",
      fontSize: "clamp(1.4rem, 2.2vw, 2rem)",
      textShadow: "0 10px 26px rgba(0,0,0,0.35)",
    },
    ctaText: { margin: "10px 0 0 0", color: "#dbeafe", lineHeight: 1.85, fontSize: 15, maxWidth: 760 },
    footer: { padding: "26px 0 40px", color: theme.muted, fontSize: 13 },
  };

  return (
    <div style={styles.page}>
      {/* Top bar (optional) */}
      <div style={styles.topBar}>
        <Container>
          <div style={styles.topBarInner}>
            <div className="d-flex gap-3 flex-wrap">
              <span>
                Email:{" "}
                <a style={styles.topLink} href={`mailto:${brand.email}`}>
                  {brand.email}
                </a>
              </span>
              <span>
                Sales:{" "}
                <a style={styles.topLink} href={`tel:${brand.salesPhone.replace(/\s/g, "")}`}>
                  {brand.salesPhone}
                </a>
              </span>
            </div>
            <div style={{ fontWeight: 900 }}>GSTIN: {brand.gst}</div>
          </div>
        </Container>
      </div>

      {/* Local product nav (optional; remove if global Navbar is enough) */}
      <div style={styles.navWrap}>
        <Container>
          <div style={styles.navInner}>
            <div style={styles.brandRow}>
              <div style={styles.logo} aria-hidden="true" />
              <div>
                <p style={styles.brandName}>{product.name}</p>
                <p style={styles.brandSub}>{product.tagline}</p>
              </div>
            </div>

            <Nav className="d-none d-md-flex" style={{ gap: 14 }}>
              <Nav.Link onClick={() => scrollToId("overview")} style={{ color: theme.slate, fontWeight: 850 }}>
                Overview
              </Nav.Link>
              <Nav.Link onClick={() => scrollToId("features")} style={{ color: theme.slate, fontWeight: 850 }}>
                Highlights
              </Nav.Link>
              <Nav.Link onClick={() => scrollToId("modules")} style={{ color: theme.slate, fontWeight: 850 }}>
                Modules
              </Nav.Link>
              <Nav.Link onClick={() => scrollToId("contact")} style={{ color: theme.slate, fontWeight: 850 }}>
                Contact
              </Nav.Link>
            </Nav>

            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                style={{ borderRadius: 12, fontWeight: 900 }}
                onClick={() => navigate("/request-demo")}
              >
                Request Demo
              </Button>
              <Button
                variant="primary"
                style={{ borderRadius: 12, fontWeight: 950, background: theme.accent, borderColor: theme.accent }}
                onClick={() => navigate("/contact-sales")}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {/* HERO */}
        <div style={styles.hero}>
          <img src={web5} alt="Jewellery billing and inventory software banner" style={styles.heroImg} />
          <div style={styles.heroOverlay} />

          <div style={styles.heroContent}>
            <Row className="w-100 g-4 align-items-center">
              <Col lg={7}>
                <div style={styles.pill}>JEWELLERY • GST BILLING • INVENTORY</div>
                <h1 style={styles.h1}>{product.name}</h1>
                <p style={styles.heroP}>{product.description}</p>

                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="primary"
                    style={{ borderRadius: 12, fontWeight: 950, background: theme.accent2, borderColor: theme.accent2 }}
                    onClick={() => navigate("/request-demo")}
                  >
                    Book a Demo
                  </Button>
                  <Button variant="light" style={{ borderRadius: 12, fontWeight: 950 }} onClick={() => scrollToId("modules")}>
                    View Modules
                  </Button>

                  <Badge bg="light" text="dark" style={{ borderRadius: 999, padding: "10px 12px", fontWeight: 900 }}>
                    {product.price}
                  </Badge>
                </div>

                <div style={styles.tagRow}>
                  {heroTags.map((t) => (
                    <span key={t} style={styles.tag}>
                      {t}
                    </span>
                  ))}
                </div>
              </Col>

              <Col lg={5}>
                <Card style={styles.heroCard}>
                  <Card.Body style={{ padding: 18 }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div style={{ fontWeight: 950, color: theme.ink, fontSize: 15 }}>Executive Snapshot</div>
                        <div style={{ color: theme.muted, fontWeight: 750, fontSize: 12, marginTop: 6 }}>
                          Built for jewellery business accuracy
                        </div>
                      </div>
                      <Badge bg="primary" style={{ borderRadius: 999, fontWeight: 900 }}>
                        GST Ready
                      </Badge>
                    </div>

                    <div style={styles.statGrid}>
                      {trustStats.map((s) => (
                        <div key={s.label} style={styles.stat}>
                          <p style={styles.statLabel}>{s.label}</p>
                          <p style={styles.statValue}>{s.value}</p>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginTop: 12, color: theme.muted, fontSize: 13, lineHeight: 1.7 }}>
                      <b style={{ color: theme.ink }}>Sales:</b>{" "}
                      <a href={`tel:${brand.salesPhone.replace(/\s/g, "")}`} style={{ textDecoration: "none" }}>
                        {brand.salesPhone}
                      </a>
                      <br />
                      Barcode / QR generation supported for items and invoices.
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </div>

        {/* OVERVIEW */}
        <section id="overview" style={styles.section}>
          <Row className="g-4 align-items-start">
            <Col lg={5}>
              <h2 style={styles.sectionTitle}>Overview</h2>
              <p style={styles.sectionSub}>
                JewSof is a trusted Jewellery Management System covering billing, inventory and accounting.
                With strong reporting and secure transaction flow, it helps you analyze business performance and improve sales and profitability.
              </p>
            </Col>

            <Col lg={7}>
              <Row className="g-3">
                {highlights.map((h) => (
                  <Col md={6} key={h.title}>
                    <div style={styles.featureCard}>
                      <div className="d-flex align-items-center justify-content-between">
                        <h3 style={styles.featureTitle}>{h.title}</h3>
                        <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 999, background: theme.accent2 }} />
                      </div>
                      <p style={styles.featureDesc}>{h.desc}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </section>

        {/* MODULES */}
        <section id="modules" style={{ ...styles.section, paddingTop: 0 }}>
          <Row className="mb-3">
            <Col lg={8}>
              <h2 style={styles.sectionTitle}>Modules & Reports</h2>
              <p style={styles.sectionSub}>
                End-to-end workflows for karigar operations, sales, purchases, stock control, outstanding tracking and accounting.
              </p>
            </Col>
          </Row>

          <Row className="g-3">
            {featureGroups.map((g) => (
              <Col lg={6} key={g.title}>
                <Card style={styles.moduleCard}>
                  <div style={styles.moduleHead}>
                    <h3 style={styles.moduleTitle}>{g.title}</h3>
                  </div>

                  <ul style={styles.moduleList}>
                    {g.bullets.map((b) => (
                      <li key={b} style={styles.moduleItem}>
                        <span
                          aria-hidden="true"
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: theme.success,
                            marginTop: 6,
                            flexShrink: 0,
                          }}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* CTA */}
        <section id="contact" style={{ paddingBottom: 56 }}>
          <div style={styles.cta}>
            <div style={styles.ctaOverlay} />
            <div style={styles.ctaBody}>
              <div>
                <h2 style={styles.ctaTitle}>Want a demo for your showroom?</h2>
                <p style={styles.ctaText}>
                  Call sales at{" "}
                  <a
                    href={`tel:${brand.salesPhone.replace(/\s/g, "")}`}
                    style={{ color: "#fff", fontWeight: 900, textDecoration: "underline" }}
                  >
                    {brand.salesPhone}
                  </a>{" "}
                  or submit a demo request.
                </p>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Button variant="light" style={{ borderRadius: 12, fontWeight: 950 }} onClick={() => navigate("/request-demo")}>
                  Request Demo
                </Button>
                <Button
                  variant="primary"
                  style={{ borderRadius: 12, fontWeight: 950, background: theme.accent2, borderColor: theme.accent2 }}
                  onClick={() => navigate("/contact-sales")}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </div>
        </section>
      </Container>
    </div>
  );
};

export default JewSof;
